import { useState, useReducer } from "react";
import Button from "@Components/Button";
import Tab from "@Components/Tabs/Tab";
import Tabs from "@Components/Tabs/Tabs";
import TabsList from "@Components/Tabs/TabsList";
import Panel from "@Pages/Search/components/NearbySection/components/Panel";
import styles from "./fitlerSubsection2.module.scss";
import UniversityDropdown from "./components/university";
import { AppState } from "@Store/index";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import filtersModalReducer from "@Pages/Search/components/FilterSection/components/FiltersModal/filtersModalReducer";
import { applyFilter } from "@Pages/Search/data/searchPageReducer";
import { SEARCH_SUBSECTION_FILTERS2 } from "@Pages/Search/components/FilterSection/data/filtersList";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import getFilterActiveStatus, {
  FilterNameType,
  getUniversityAppliedStatus,
  getDistanceFromCityCenterStatus,
} from "@Pages/Search/utils/getFilterActiveStatus";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import StayDuration from "./components/stayDuration";
import DistanceFromCityCenter from "./components/distanceFromCityCenter";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE, FILTER_FLOW, FILTER_MODE } from "@Utils/webApputils/analyticEvents";

const FilterSubsection2 = ({ isDesktop = false }) => {
  const searchListDispatch = useDispatch();

  const { filters } = useSelector(
    ({ searchPageData, appData }: AppState) => ({
      dynamicFilterCountData: searchPageData.dynamicFilterCountData,
      filters: searchPageData.filters,
      priceRange: searchPageData?.region?.region_inventory_price_range,
      isDesktop: appData.isDesktop,
    }),
    shallowEqual,
  );

  const { t } = useTranslationHook();

  const [currFilters, dispatch] = useReducer(filtersModalReducer, filters);
  const [forceReset, setForceReset] = useState(0);

  const filtersActiveStatus =
    getFilterActiveStatus("Filters" as FilterNameType, currFilters) ||
    getUniversityAppliedStatus(currFilters) ||
    getDistanceFromCityCenterStatus(currFilters);

  const handleApplyFilters = () => {
    const normalizedFilters: Record<string, any> = {};
    if (currFilters?.university) {
      normalizedFilters.university = currFilters?.university;
    }
    if (currFilters?.minLeaseDuration || currFilters?.maxLeaseDuration) {
      normalizedFilters.stayDuration = [
        currFilters?.minLeaseDuration ?? null,
        currFilters?.maxLeaseDuration ?? null,
      ];
    }
    if (currFilters?.region_radius) {
      normalizedFilters.distanceFromCityCenter = currFilters?.region_radius;
    }
    const appliedFilterTypes = Object.keys(normalizedFilters)?.join(",");
    logAppEvent({
      eventName: APP_EVENTS.FILTER_APPLIED,
      payload: {
        filter_flow: FILTER_FLOW.INLINE_FILTER,
        mode: FILTER_MODE.LISTING_VIEW,
        screenName: APP_PAGE.SEARCH,
        filterType: appliedFilterTypes,
        filterValue: normalizedFilters,
      },
    });

    gtagEventEmitter({
      action: "SP_filter_subsection_filter_apply_click",
      category: "SP_filter_subsection",
      value: true,
    });
    searchListDispatch(applyFilter(currFilters));
  };

  const handleResetFilters = () => {
    dispatch({
      type: "applyAll",
      payload: {},
    });
    setForceReset((v) => v + 1);
  };

  const handleFilterSelect = (filterName: string) => {
    gtagEventEmitter({
      action: "SP_filter_subsection_filter2_click",
      category: "SP_filter_subsection2",
      value: filterName,
    });
  };

  return (
    <div
      data-testid="Inline-Filter-V2"
      className={formatClassNames(styles.subsectionContainer, isDesktop && styles.desktop)}
    >
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>
            {t("searchpage.searchlist.filtersubsection2.filters")}&nbsp;
          </span>
          <span className={styles.title_highlight}>
            {t("searchpage.searchlist.filtersubsection2.foryou")}
          </span>
        </div>
        <button
          type="button"
          className={formatClassNames(styles.reset, !filtersActiveStatus && styles.disabled)}
          onClick={handleResetFilters}
          disabled={!filtersActiveStatus}
        >
          {t("searchpage.searchlist.filtersubsection2.reset")}
        </button>
      </div>

      {/* Filter Tabs */}
      <Tabs
        defaultValue={t("searchpage.searchlist.filtersubsection.university")}
        className={formatClassNames(styles.tabs, isDesktop && styles.desktop)}
      >
        <TabsList className={styles.tabList}>
          {SEARCH_SUBSECTION_FILTERS2.map(({ name, key, translationKey }) => (
            <Tab
              className={styles.tab}
              value={t(translationKey)}
              key={key}
              onClick={() => handleFilterSelect(name)}
            >
              {t(name)}
            </Tab>
          ))}
        </TabsList>

        <Panel name={t("searchpage.searchlist.filtersubsection.university")}>
          <UniversityDropdown dispatch={dispatch} value={currFilters.university} />
        </Panel>
        <Panel name={t("searchpage.searchlist.filtersubsection.stayduration")}>
          <StayDuration
            key={forceReset}
            dispatch={dispatch}
            value={currFilters?.minLeaseDuration as string}
            isInlineFilters
          />
        </Panel>
        <Panel name={t("searchpage.searchlist.filtersubsection.distancefromcitycentre")}>
          <DistanceFromCityCenter dispatch={dispatch} value={currFilters?.region_radius} />
        </Panel>
      </Tabs>
      <div className={formatClassNames(styles.btn, isDesktop && styles.desktop)}>
        <Button
          variant="primary"
          className={formatClassNames(styles.applyFilterBtn, isDesktop && styles.desktop)}
          onClick={handleApplyFilters}
          greyDisabled={!filtersActiveStatus}
          size="md"
        >
          {t("searchpage.searchlist.filtersubsection2.applyfilter")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(FilterSubsection2, {
  componentName: "FilterSubsection2-SearchListDesktop-SearchPage",
  showForChina: true,
  showForWhiteLable: false,
});
